import { useEventsQuery } from '@/state/queries/events/use-events.ts';
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Typography,
  InputAdornment,
  TextField,
  IconButton,
  Button,
} from '@mui/material';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useTranslations } from '@/i18n/use-translations';
import { useInView } from 'react-intersection-observer';
import { useEffect, useState, useRef, useMemo, type MouseEvent } from 'react';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { Close, AddOutlined } from '@mui/icons-material';
import PageHeaderBar from '@/components/common/page-header-bar';
import EventForm from '@/components/create-wizard/tab-row-item/row-forms/create-event.form';
import { useNavigate, useLocation } from 'react-router';
import EventCatalogProductsListModal from '@/components/events/event-catalog/event-catalog-products-list.modal.tsx';
import { type ProductKey } from '@/types/dtos/orders/response/product.dto.ts';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal.tsx';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import { useGetProductsQuery } from '@/state/queries/orders/use-get-products.ts';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import { ORDER_STATUS } from '@/types/dtos/orders/response/order.dto.ts';
import EventListTableRow from '@/components/events/event-list-table-row';
import { getOrderLineItemProductId } from '@/helpers/order-line-item.helper';
import { useDebounce } from '@/hooks/use-debounce.ts';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import CreateOverlay from '@/components/common/create-overlay';
import { useCatalogDisplayFeatureAccess, useFeatureAccess } from '@/hooks/use-feature-access';
import { filterProductsForEventCatalogUi } from '@/constants/product-catalog.ts';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

export default function EventList({ organizationId }: { organizationId: string }) {
  const t = useTranslations();
  const location = useLocation();
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const [selectedEvent, setSelectedEvent] = useState<EventDto | undefined>(undefined);
  const [isCatalogPickerOpen, setIsCatalogPickerOpen] = useState(false);
  const [restoreCheckoutWhenPickerCloses, setRestoreCheckoutWhenPickerCloses] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [expandedProductKey, setExpandedProductKey] = useState<ProductKey | undefined>(undefined);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { data, isFetching, hasNextPage, fetchNextPage } = useEventsQuery(organizationId, debouncedSearchTerm);
  const { data: catalogProducts } = useGetProductsQuery();
  const navigate = useNavigate();
  const setProducts = useProductsStore((s) => s.setProducts);
  const isLargeScreen = useBreakpointUp('lg');
  const { hasAccess: hasMonetizeAccess, isLoading: monetizeAccessLoading } = useFeatureAccess('monetize');
  const { hasAccess: hasCatalogOrderAccess, isLoading: catalogOrderAccessLoading } =
    useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);
  const showMonetizeCatalog =
    !monetizeAccessLoading && !catalogOrderAccessLoading && hasMonetizeAccess && hasCatalogOrderAccess;
  const { featureAccess: catalogFeatureAccess } = useCatalogDisplayFeatureAccess();
  const monetizeCatalogProducts = useMemo(
    () => filterProductsForEventCatalogUi(catalogProducts, { featureAccess: catalogFeatureAccess }),
    [catalogProducts, catalogFeatureAccess],
  );

  // Determine if current route is organization-related or user-related
  const isOrganizationRoute = location.pathname.startsWith('/organization/');

  // Determine current menu item based on pathname
  const currentMenuItem = useMemo(() => {
    const pathname = location.pathname;
    if (pathname.includes('/events')) {
      return t('common.events');
    } else if (pathname.includes('/services')) {
      return t('common.services');
    } else if (pathname.includes('/venues')) {
      return t('common.venues');
    } else if (pathname.includes('/equipment')) {
      return t('common.equipment');
    } else if (pathname === '/' || pathname === '/dashboard') {
      return t('common.dashboard');
    }
    // Default to Events
    return t('common.events');
  }, [location.pathname, t]);

  useEffect(() => {
    if (catalogProducts) {
      setProducts(catalogProducts);
    }
  }, [catalogProducts, setProducts]);

  const { ref, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetching, fetchNextPage]);

  const toggleRowExpansion = (eventId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const handleCatalogButtonClick = (
    clickEvent?: MouseEvent<HTMLButtonElement>,
    eventDto?: EventDto,
    productKey?: ProductKey,
  ) => {
    clickEvent?.stopPropagation();
    setSelectedEvent(eventDto || selectedEvent);

    const eventToCheck = eventDto || selectedEvent;

    const draftOrder = eventToCheck?.orders?.find(
      (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
    );
    const catalogProductIdsInCart = new Set(
      (draftOrder?.lineItems ?? [])
        .map((item) => getOrderLineItemProductId(item.product))
        .filter((id): id is string => id !== undefined),
    );
    const allCatalogProductsInCart =
      monetizeCatalogProducts.length > 0 && monetizeCatalogProducts.every((pf) => catalogProductIdsInCart.has(pf.id));

    if (allCatalogProductsInCart && draftOrder) {
      setIsCheckoutModalOpen(true);
      setRestoreCheckoutWhenPickerCloses(true);
      setIsCatalogPickerOpen(false);
    } else {
      setExpandedProductKey(productKey);
      setIsCatalogPickerOpen(true);
      setIsCheckoutModalOpen(false);
    }
  };

  const handleCheckoutRequestedFromCatalog = () => {
    setIsCheckoutModalOpen(true);
    setRestoreCheckoutWhenPickerCloses(true);
    setIsCatalogPickerOpen(false);
  };

  const handleCatalogPickerClose = () => {
    setIsCatalogPickerOpen(false);
    setIsCheckoutModalOpen(restoreCheckoutWhenPickerCloses);
  };

  const handleCheckoutModalClose = () => {
    setIsCheckoutModalOpen(false);
    setRestoreCheckoutWhenPickerCloses(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchExpand = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchExpanded = () => {
    // Focus the input after Collapse animation completes
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      });
    });
  };

  const handleSearchCollapse = () => {
    setIsSearchExpanded(false);
    setSearchTerm('');
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleSearchCollapse();
    }
  };

  const hasEvents = data?.pages.some((page) => page.data.length > 0) ?? false;
  const showCreateOverlay = !isFetching && !hasEvents && !debouncedSearchTerm;

  const searchContent = (
    <TextField
      variant="outlined"
      placeholder={t('common.search')}
      value={searchTerm}
      onChange={handleSearchChange}
      onKeyDown={handleSearchKeyDown}
      fullWidth
      size="small"
      inputRef={searchInputRef}
      sx={{
        '& .MuiOutlinedInput-root': {
          'borderRadius': 2,
          'backgroundColor': 'background.default',
          'transition': 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
          },
        },
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSearchCollapse} size="small" sx={{ color: 'text.secondary' }}>
                <Close />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );

  const mainContent = (
    <>
      {/* Breadcrumb */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          maxWidth: { xs: 'none', sm: '300px', md: '500px' },
          minWidth: 0,
          flex: { xs: '1 1 auto', sm: '0 1 auto' },
        }}
      >
        {isOrganizationRoute && activeOrganization && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1,
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
            }}
          >
            {activeOrganization.name}
          </Typography>
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: isOrganizationRoute ? 400 : 600,
            color: isOrganizationRoute ? 'text.secondary' : 'text.primary',
            flexShrink: 0,
            ml: isOrganizationRoute ? 1 : 0,
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
          }}
        >
          {isOrganizationRoute && activeOrganization ? '/ ' : ''}
          {currentMenuItem}
        </Typography>
      </Box>

      {/* Desktop: persistent search field */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
          width: 300,
          ml: 'auto',
          flexShrink: 0,
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          variant="standard"
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          size="small"
          sx={{
            '& .MuiInput-root': {
              'transition': 'all 0.2s ease-in-out',
              '&:hover:not(.Mui-disabled):before': {
                borderBottomColor: 'action.active',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        {isLargeScreen ? (
          <Button
            variant="outlined"
            startIcon={<AddOutlined />}
            onClick={() => setIsCreateEventModalOpen(true)}
            size="small"
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {t('event.title').toUpperCase()}
          </Button>
        ) : (
          <IconButton
            onClick={() => setIsCreateEventModalOpen(true)}
            sx={{
              'color': 'text.secondary',
              'flexShrink': 0,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <AddOutlined />
          </IconButton>
        )}
      </Box>

      {/* Mobile/Small/Medium: search icon button and create button */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' },
          gap: 0.5,
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={handleSearchExpand}
          sx={{
            color: 'text.secondary',
            ml: { xs: 0.5, sm: 1 },
          }}
        >
          <SearchIcon />
        </IconButton>
        <IconButton
          onClick={() => setIsCreateEventModalOpen(true)}
          sx={{
            'color': 'text.secondary',
            'mr': { xs: 0.5, sm: 1 },
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <AddOutlined />
        </IconButton>
      </Box>
    </>
  );

  return (
    <>
      <Stack
        width="100%"
        spacing={0}
        pt={0}
        pb={1}
        bgcolor="background.default"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <PageHeaderBar
          searchExpanded={isSearchExpanded}
          searchContent={searchContent}
          mainContent={mainContent}
          onSearchExpanded={handleSearchExpanded}
        />

        {showCreateOverlay ? (
          <Box sx={{ px: { xs: 0, sm: 2 }, flex: 1, overflow: 'auto' }}>
            <CreateOverlay />
          </Box>
        ) : (
          <Box
            sx={{
              px: { xs: 0, sm: 2 },
              width: '100%',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TableContainer
              sx={{
                width: '100%',
                flex: 1,
                overflowX: 'auto',
                overflowY: 'auto',
                maxWidth: '100%',
              }}
            >
              <Table
                stickyHeader
                sx={{
                  'width': '100%',
                  'tableLayout': 'auto',
                  '& .icon-column': {
                    width: '40px',
                    paddingRight: 0,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableHead sx={{ display: { xs: 'none', lg: 'table-header-group' } }}>
                  <TableRow>
                    <TableCell className="icon-column" />
                    <TableCell>
                      <Typography variant="subtitle1">{t('event.title')}</Typography>
                    </TableCell>
                    {isLargeScreen && showMonetizeCatalog && (
                      <TableCell sx={{ maxWidth: 0, width: '40%' }}>
                        <Typography variant="subtitle1">{t('common.features')}</Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data?.pages.map((page) =>
                    page.data.map((event) => (
                      <EventListTableRow
                        key={event.id}
                        event={event}
                        catalogProducts={showMonetizeCatalog ? monetizeCatalogProducts : (catalogProducts ?? [])}
                        isLargeScreen={isLargeScreen}
                        showMonetizeCatalog={showMonetizeCatalog}
                        expandedRows={expandedRows}
                        toggleRowExpansion={toggleRowExpansion}
                        navigate={navigate}
                        onCatalogButtonClick={handleCatalogButtonClick}
                      />
                    )),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Box ref={ref} sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {isFetching && <CircularProgress size={24} />}
        </Box>
      </Stack>
      <EventCatalogProductsListModal
        event={selectedEvent}
        open={isCatalogPickerOpen}
        expandedProductKey={expandedProductKey}
        onCheckoutRequested={handleCheckoutRequestedFromCatalog}
        onModalClose={handleCatalogPickerClose}
      />
      <EventCatalogOrderCheckoutModal
        open={isCheckoutModalOpen}
        event={selectedEvent}
        onModalClose={handleCheckoutModalClose}
        onOpenCatalogPicker={handleCatalogButtonClick}
      />
      {isCreateEventModalOpen && (
        <EventForm
          onClose={() => setIsCreateEventModalOpen(false)}
          onSuccess={() => setIsCreateEventModalOpen(false)}
        />
      )}
    </>
  );
}
